import { useEffect } from 'react'
import { setCookie } from 'cookies-next'
import { IncomingMessage, ServerResponse } from 'http'
import { ParsedUrlQuery } from 'querystring'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { ServerExperiments } from '@/experimentation'
import {
  ExperimentParamsArray,
  runContentfulExperimentOnServer,
  runExperimentsOnServer,
} from '@/experimentation/server'
import { Seo, getBasicPageMetaData } from '@/layout/Seo'
import { DefaultLayout } from '@/layouts/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { TvAuthToast } from '@/molecules/TvAuthToast'
import { useLegacyEffect } from '@/molecules/utils'
import { GuildGeogatedBenefitsPrompt } from '@/organisms/GuildGeogatedBenefitsPrompt'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import { getContentfulClient } from '@/services/ApolloClient'
import { GUILD_PREDICTS_WINNERS_ENTRY_ID, getCollection } from '@/services/CmsService/Collection'
import { Promotion, getGuildPromotion } from '@/services/GuildPromotionService'
import {
  getExplainerLayout,
  getFeaturedSection,
  getGuildPricingTicketsOption,
  getGuildSignupPageBySlug,
  getTestimonialSection,
  getTicketsSection,
  GuildPricingTicketsOption,
  GuildSignupPageData,
} from '@/services/GuildSignupPageService'
import {
  GuildProjectSlugs,
  GuildSignupEmphasis,
  GuildSignupLayout,
} from '@/services/GuildSignupPageService/GuildSignupPageTypes'
import { isGuildSignupSimpleLayout } from '@/services/GuildSignupPageService/validations'
import { getGuildStatus } from '@/services/GuildUserService/GuildUserService'
import { FETCH_USER_MEMBERSHIP, hasOnlyGiftedMembership } from '@/services/MembershipService/MembershipService'
import { isSubPurchaseBlocked } from '@/services/MembershipService/membership-status'
import { getProjectSeoMetadata, getWatchableContentByGuid, ProjectSeoMetadata } from '@/services/ProjectsService'
import { getProjectLogoUrl } from '@/services/ProjectsService/ProjectsService'
import { GuildSale, getGuildSale } from '@/services/SaleService'
import {
  Collection,
  GuildFeaturedSection,
  GuildSignupExplainerLayout,
  GuildSignupTicketsSection,
  GuildTestimonialSection,
  Maybe,
  type Seo as SeoType,
} from '@/types/codegen-contentful'
import { FetchUserMembershipQuery } from '@/types/codegen-federation'
import { buildCloudinaryImageUrlFromContentfulObject } from '@/utils/Cloudinary'
import { shouldShowAvailabilityWarning } from '@/utils/GuildUtil'
import { useSafeTrack } from '@/utils/analytics'
import { useStickyAttribution } from '@/utils/analytics/StickyAttribution'
import { projectSlugsAttribution } from '@/utils/analytics/attribution'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { writeToSessionStorage } from '@/utils/session-storage'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { GuildStatus } from '@/utils/users/usersServer'
import { GuildSignupView } from '@/views/GuildSignupView'
import { GuildSignupContextProvider } from '@/views/GuildSignupView/GuildSignupContext'

interface GuildSignupProps {
  projectLogoUrl: string | null
  emphasis: GuildSignupEmphasis
  guildMemberCta: string
  guildMemberHref: string
  guildExplainerLayout?: GuildSignupExplainerLayout | null
  layout: GuildSignupLayout
  locale: string
  metadata?: ProjectSeoMetadata | null
  projectSlugs?: GuildProjectSlugs
  promotion: Promotion | null
  guildFeaturedSection?: GuildFeaturedSection | null
  sale: GuildSale | null
  saleNameOverride?: string | null
  seo?: Maybe<SeoType>
  slug: string
  votingPhoneImageUrl?: string | null
  guildTestimonialSection?: GuildTestimonialSection
  guildTicketsSection?: GuildSignupTicketsSection
  guildPredictsWinners?: Collection
  hasUser: boolean
  logo?: {
    src: string
    alt: string
    className?: string
  }
  experiments: ServerExperiments
  region: string | null
  hideGeogatedPrompt: boolean
  isDarkMode?: boolean
  guildPricingTicketsOption?: Maybe<GuildPricingTicketsOption>
  hideAngelSections?: boolean
}

export const GuildSignupPage: NextPageWithLayout<GuildSignupProps> = ({
  projectLogoUrl,
  emphasis,
  guildMemberCta,
  guildMemberHref,
  guildExplainerLayout,
  layout,
  locale,
  metadata,
  projectSlugs,
  promotion,
  sale,
  seo,
  slug,
  votingPhoneImageUrl,
  guildTestimonialSection,
  guildTicketsSection,
  guildPredictsWinners,
  hasUser,
  logo,
  experiments,
  guildFeaturedSection,
  hideGeogatedPrompt,
  region,
  guildPricingTicketsOption,
  hideAngelSections,
}) => {
  const { t } = useTranslate('guild')
  const track = useSafeTrack()

  useLegacyEffect(() => {
    track('Guild Product Page Viewed', { pageContext: emphasis, slug })
    setCookie('guildFocus', slug, { maxAge: Durations.ONE_YEAR_IN_SECONDS })
  }, [track, slug, emphasis])

  useEffect(() => {
    if (slug) {
      writeToSessionStorage('pageSlug', slug)
      writeToSessionStorage('guildCheckoutOrigin', `/guild/join/${slug}`)
      writeToSessionStorage('guildEcommerceProductId', slug)
    }
  }, [slug])

  useEffect(() => {
    if (projectSlugs) {
      projectSlugsAttribution(projectSlugs)
    }
  }, [projectSlugs])

  useStickyAttribution('guild-attribution')

  const angelGuildPages = new Set(['angel-studios', 'sale', 'monthly', 'yearly', 'angel', 'angel-studios-test'])
  const showSocialProof = angelGuildPages.has(slug)
  const seoTitle =
    seo?.title ??
    (metadata?.name
      ? `${t('joinToWatch', 'Join To Watch')} ${metadata.name} | Angel`
      : t('angelGuildEarlyAccessMetaTitleV2', 'Angel Guild Membership | Angel'))
  const seoDescription =
    seo?.description ??
    metadata?.description?.trim() ??
    t(
      'angelStudiosGuildJoinInfoMetaDescriptionV3',
      'Join the Angel Guild to vote on upcoming shows, get free tickets to theatrical releases, early access, and merchandise discounts.',
    )
  const seoImage = seo?.metaImage2?.[0]
    ? buildCloudinaryImageUrlFromContentfulObject(seo?.metaImage2?.[0], 'w_1200')
    : metadata?.imagePath ?? 'v1736351150/angel-studios/guild/amgel-guild-open-graph-241030.webp'

  return (
    <div className={isGuildSignupSimpleLayout(layout) ? 'bg-white' : 'bg-core-gray-950'}>
      <Seo
        {...getBasicPageMetaData({
          cloudinaryImagePath: seoImage,
          cloudinaryTransformation: 'b_rgb:000000',
          description: seoDescription,
          locale,
          path: `${paths.guild.join}/${slug}`,
          title: seoTitle,
        })}
      />
      <GuildSignupContextProvider
        projectLogoUrl={projectLogoUrl}
        guildTestimonialSection={guildTestimonialSection}
        guildTicketsSection={guildTicketsSection}
        emphasis={emphasis}
        guildExplainerLayout={guildExplainerLayout}
        guildMemberCta={guildMemberCta}
        guildMemberHref={guildMemberHref}
        layout={layout}
        projectSlugs={projectSlugs}
        promotion={promotion}
        sale={sale}
        slug={slug}
        votingPhoneImageUrl={votingPhoneImageUrl}
        logo={logo}
        experiments={experiments}
        guildPredictsWinners={guildPredictsWinners}
        region={region}
        showSocialProof={showSocialProof}
        guildPricingTicketsOption={guildPricingTicketsOption}
        guildFeaturedSection={guildFeaturedSection}
        hideAngelSections={hideAngelSections}
      >
        <GuildSignupView hasUser={hasUser} />
      </GuildSignupContextProvider>
      <MarketingVideoPopup ctaHref={'#plan-selection'} ctaText={t('joinTheGuild', 'Join the Guild')} />
      <TvAuthToast />
      {!hideGeogatedPrompt && <GuildGeogatedBenefitsPrompt slug={slug} />}
    </div>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler(
  async ({ federationClient, locale, nextRedirect, params, preview, query, req, res, userId, region }) => {
    const isSupportLink = query.special === 'last-chance'
    const slug = getItemOrFirstEntry(params?.slug)
    if (!slug) {
      return { redirect: { destination: paths.guild.join, permanent: false } }
    }

    const opts = { locale, preview }
    const contentfulClient = getContentfulClient({ preview, locale })
    const [pageData, guildStatus, metadata, membershipData] = await Promise.all([
      getGuildSignupPageBySlug({ contentfulClient, slug, opts }),
      getGuildStatus(federationClient),
      getProjectSeoMetadata(slug, federationClient),
      userId
        ? federationClient
            .query<FetchUserMembershipQuery>({ query: FETCH_USER_MEMBERSHIP, context: { requireAuth: true } })
            .then((result) => result.data?.user?.myMembership ?? null)
            .catch(() => null)
        : Promise.resolve(null),
    ])

    const hasGiftedOnly = hasOnlyGiftedMembership(membershipData)

    // Redirect subscribers who cannot purchase to their membership management page
    if (isSubPurchaseBlocked(membershipData)) {
      return nextRedirect(paths.account.guildMembership)
    }

    // If Guild Member and redirected from home, push to /watch instead (unless they only have a gifted membership)
    if (slug === 'sale' && query.origin && query.origin === 'home' && guildStatus?.isGuildMember && !hasGiftedOnly) {
      return {
        redirect: {
          destination: '/watch',
          permanent: false,
        },
      }
    }

    if (!pageData) {
      return { redirect: { destination: paths.guild.join, permanent: false } }
    }
    if (pageData.redirectTo) {
      return { redirect: { destination: pageData.redirectTo, permanent: false } }
    }

    const emphasis = pageData.emphasis
    if (shouldRedirectToDashboard(guildStatus, emphasis) && !hasGiftedOnly) {
      return {
        redirect: {
          destination: getDestinationUrl(pageData, emphasis, query),
          permanent: false,
        },
      }
    }

    const layoutExperiment = await runContentfulExperimentOnServer(
      req,
      res,
      pageData.layoutExperimentCollection?.items ?? [],
    )
    const layout = layoutExperiment ?? pageData.layout // need to support both layout and layoutExperiment for older pages
    const seo = pageData.seo

    const guildExplainerLayout = getExplainerLayout(pageData)
    const guildTestimonialSection = getTestimonialSection(pageData)
    const guildTicketsSection = getTicketsSection(pageData)
    const guildFeaturedSection = getFeaturedSection(pageData)

    const projectSlug = pageData?.project?.slug

    const [
      sale,
      promotion,
      guildPricingTicketsOption,
      guildPredictsWinners,
      projectLogoUrl,
      experiments,
      translations,
    ] = await Promise.all([
      getGuildSale({ opts: { preview, locale }, federationClient }),
      getGuildPromotion({ opts: { locale, preview }, isSupportLink, federationClient }),
      getGuildPricingTicketsOption({ contentfulClient, opts }),
      getCollection({ locale, preview }, GUILD_PREDICTS_WINNERS_ENTRY_ID),
      projectSlug ? getProjectLogoUrl(projectSlug, federationClient) : Promise.resolve(null),
      runExperiments({
        req,
        res,
        slug,
      }),
      loadTranslations(locale, ['common', 'join-the-guild', 'guild', 'guild-benefits', 'app-promo', 'watch']),
    ])

    // For non-US users with a project title, check if the content is actually region-blocked.
    // If available in their region, suppress the geo-gated prompt. For US users or non-project
    // pages (e.g. "sale"), the component's own shouldShowAvailabilityWarning check handles visibility.
    const isOutsideUS = shouldShowAvailabilityWarning(region)
    let hideGeogatedPrompt = false
    if (isOutsideUS && metadata?.titleId) {
      const watchableContent = await getWatchableContentByGuid(metadata.titleId, federationClient)
      hideGeogatedPrompt = watchableContent?.watchableAvailabilityStatus !== 'REGION_BLOCKED'
    }

    return {
      props: {
        projectLogoUrl,
        emphasis,
        guildExplainerLayout,
        guildFeaturedSection,
        guildMemberCta: pageData.guildMemberCta,
        guildMemberHref: pageData.guildMemberHref,
        layout,
        locale,
        projectSlugs: pageData.projectSlugs,
        promotion,
        sale,
        saleNameOverride: pageData.saleNameOverride ?? null,
        seo,
        slug,
        votingPhoneImageUrl: pageData.votingPhoneImageUrl ?? null,
        logo: pageData?.project?.logo ?? null,
        guildTestimonialSection,
        guildTicketsSection,
        guildPredictsWinners: guildPredictsWinners ?? null,
        hasUser: Boolean(userId),
        metadata,
        experiments,
        hideGeogatedPrompt,
        hideAngelSections: pageData.hideAngelSections ?? false,
        isDarkMode: layout?.__typename !== 'GuildSignupSimpleLayout',
        region,
        guildPricingTicketsOption,
        ...translations,
      },
    }
  },
)

GuildSignupPage.getLayout = (page, pageProps) => {
  return (
    <DefaultLayout isDarkMode={Boolean(pageProps?.isDarkMode)} saleNameOverride={pageProps?.saleNameOverride}>
      {page}
    </DefaultLayout>
  )
}

export default GuildSignupPage

function shouldRedirectToDashboard(guildStatus: GuildStatus | null, emphasis: GuildSignupEmphasis) {
  if (!guildStatus) return false
  if (emphasis === 'tickets' && guildStatus.guildRoles?.hasFreeTickets) return true
  if (emphasis === 'early-access' && guildStatus.guildRoles?.hasEarlyAccess) return true
  return false
}

function getDestinationUrl(pageData: GuildSignupPageData, emphasis: GuildSignupEmphasis, query: ParsedUrlQuery) {
  let destination = pageData.guildMemberHref ?? `/guild#${emphasis}`
  if (query['tv-post-auth'] === 'True') {
    const hasQuery = destination.includes('?')
    destination = `${destination}${hasQuery ? '&' : '?'}tv-post-auth=True`
  }
  return destination
}

async function runExperiments({
  req,
  res,
}: {
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> }
  res: ServerResponse
  slug: string
}): Promise<ServerExperiments> {
  const expConfigs: ExperimentParamsArray = [
    { experimentKey: 'thousands-joined-premium-text', defaultValue: false },
    { experimentKey: 'coupon-test-member-selection', defaultValue: 'control' },
  ]

  return await runExperimentsOnServer(req, res, expConfigs)
}
